import { orderHasVehicle } from '@models';
import { Invoice, Order } from '@services';
import { useStore } from '@stores';
import { Utils } from '@utils';
import { useCallback } from 'react';

interface IGenerateInvoice {
  /** The job the invoice takes its vehicle from. */
  order: Order;
  /** Every job on the invoice, including `order`. */
  orders: Order[];
  customerId: string;
  dueDate: number;
}

/**
 * Raises an invoice for completed jobs. A vehicle is optional: a job can have none (a voided QuickPay
 * sale goes back to Completed with a blank plate) and there is no way to add a plate to a job. Updating
 * the vehicle's mileage must not stand in the way of the invoice either, so when the vehicle can't be
 * found the invoice takes what the job itself says about it.
 */
export const useGenerateInvoice = () => {
  const { invoiceStore, vehicleStore } = useStore();

  return useCallback(
    async ({ order, orders, customerId, dueDate }: IGenerateInvoice): Promise<Invoice | null> => {
      const serviceOrders = orders.flatMap((x) => x.serviceOrders);
      // 0 when no job has a reading (a job with none has a null mileage, which Number makes 0 anyway).
      const mileage = Math.max(0, ...orders.map((x) => Number(x.mileage)).filter((m) => !isNaN(m)));

      // The API finds the vehicle by VIN (or plate when there is none) and returns it, so no
      // separate lookup is needed.
      const vehicle = orderHasVehicle(order)
        ? await vehicleStore.updateVehicleMileage({
            vin: order.vin,
            registrationNumber: order.registrationNumber,
            mileage,
          })
        : null;

      const vin = vehicle?.combinedVin || order.vin || '';

      return invoiceStore.create({
        vat: Utils.calculateTotalVat(serviceOrders),
        amount: Utils.calculateTotalAmount(serviceOrders),
        dueDate,
        vinNumber: vin,
        makeModel:
          `${vehicle?.combinedMake ?? ''} ${vehicle?.combinedModel ?? ''}`.trim() ||
          order.vehicleModel ||
          undefined,
        registrationNumber: order.registrationNumber ?? '',
        vin,
        customerId,
        orders: orders.map((x) => x.id),
        serviceOrders,
        mileage,
      });
    },
    [invoiceStore, vehicleStore]
  );
};
